import { ChartListItem, VendingPackageVolumeByUserListChart, takeRankedChartSeries } from '@frontend/chart/components';
import { Chart } from '@frontend/chart/types';
import { CommonMessage } from '@frontend/lang';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { PlatformObjectBadgeMapper } from '../../../modal/platform-object-badge/platform-object-badge-mapper.component';
import { useLast30DaysVendingPackageVolumeByUserListChart } from '../../../statistics/vending/last-30-days-vending-package-volume-by-user-list-chart/use-last-30-days-vending-package-volume-by-user-list-chart';
import { InsightCardWidth } from '../insight-card-width';
import { InsightCard } from '../insight-card/insight-card.component';
import { InsightId } from '../insight-registry/insight-ids';

export interface VendingTopConsumersListProps {
    accountId: string;
    width?: InsightCardWidth;
}

const USER_LIMIT = 5;

const toConsumerItems = (chart: Chart): ChartListItem[] =>
    chart.series.flatMap((series) => {
        const rawName = series.name?.trim();
        if (!rawName || rawName.toLowerCase() === 'default' || rawName.toLowerCase() === 'none') {
            return [];
        }

        return [
            {
                key: rawName,
                name: rawName,
                value: series.data.reduce((sum, point) => sum + point.value, 0)
            }
        ];
    });

export const VendingTopConsumersList = ({ accountId, width }: VendingTopConsumersListProps) => {
    const { formatMessage } = useIntl();
    const { data, isLoading } = useLast30DaysVendingPackageVolumeByUserListChart({ accountId });

    const consumerItems = useMemo(() => {
        if (!data) {
            return [];
        }

        const ranked = takeRankedChartSeries(data, 'highest', USER_LIMIT);
        return toConsumerItems(ranked);
    }, [data]);

    const consumedHeader = formatMessage({
        id: 'platform.statistics.vending.last-30-days-vending-package-volume-by-product-list-chart.consumed',
        description: 'Column header for the consumed quantity in the Product velocity list on the vending dashboard',
        defaultMessage: 'Consumed'
    });

    return (
        <InsightCard
            insightId={InsightId.VENDING_TOP_CONSUMERS}
            chartKey={VendingPackageVolumeByUserListChart.definition.key}
            width={width}
            title={
                <FormattedMessage
                    id='platform.statistics.vending.last-30-days-vending-package-volume-by-user-list-chart.title'
                    description='Title of the vending dashboard list showing the users with the highest consumption volume over the last 30 days'
                    defaultMessage='Top consumers'
                />
            }
            subtitle={
                <FormattedMessage
                    id='widget.last-30-days'
                    defaultMessage='last 30 days'
                />
            }
            isLoading={isLoading}>
            {consumerItems.length === 0 ? (
                <div className='text-foreground-default'>{CommonMessage.ERRORS.NO_DATA_AVAILABLE}</div>
            ) : (
                <div className='max-h-[420px] w-full min-w-0 overflow-y-auto'>
                    <div className='border-border-default text-foreground-default flex items-start gap-4 border-b text-base font-medium'>
                        <div className='w-full px-1 py-2'>{CommonMessage.OBJECTS.USER.DEFAULT}</div>
                        <div className='w-[120px] shrink-0 py-2 text-right'>{consumedHeader}</div>
                    </div>
                    {consumerItems.map((item) => (
                        <div
                            key={item.key}
                            className='border-border-default flex h-11 items-center gap-4 border-b'>
                            <div className='text-foreground-default flex w-full min-w-0 items-center px-1 text-sm font-medium'>
                                <PlatformObjectBadgeMapper
                                    id={item.key}
                                    type='user_id'
                                />
                            </div>
                            <div className='flex w-[120px] shrink-0 justify-end pe-1'>
                                <span className='text-background-surface bg-status-success inline-flex min-w-8 items-center justify-center rounded-full px-2 py-0.5 text-sm font-semibold'>
                                    {item.value}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </InsightCard>
    );
};
